import { useQuery } from "@tanstack/react-query";
import { getAtkomstStores } from "@coworker/app/src/hooks/API/core.service";
import { INTERVAL } from "@coworker/app/src/constants/clientTime";
import {
  fixaTeamVirtualStores,
  isFixaTeamVirtualStoreId,
  Store,
} from "../../shared/types/store";

// Virtual (Fixa-team) stores are only for Platform Owners.
export const filterVirtualStoresByAuthorization = <
  T extends { storeId: string },
>(
  stores: T[] | undefined,
  hasPlatformOwnerAuthorization: boolean
): T[] =>
  (stores ?? []).filter(
    ({ storeId }) =>
      hasPlatformOwnerAuthorization || !isFixaTeamVirtualStoreId(storeId)
  );

const getAtkomstStoresWithVirtualStores = async (
  hasPlatformOwnerAuthorization: boolean
) => {
  const atkomstFilteredStores = await getAtkomstStores();

  if (!hasPlatformOwnerAuthorization) {
    return filterVirtualStoresByAuthorization(atkomstFilteredStores, false);
  }

  const virtualStores = fixaTeamVirtualStores
    .map(({ baseStoreId, suffix }) => {
      const baseStore = atkomstFilteredStores.find(
        (store: Store) => store.storeId === baseStoreId
      );
      return baseStore
        ? { ...baseStore, storeId: `${baseStoreId}${suffix}` }
        : undefined;
    })
    .filter((store): store is Store => !!store);

  const synthesizedIds = new Set(virtualStores.map(({ storeId }) => storeId));
  return [
    ...virtualStores,
    ...atkomstFilteredStores.filter(
      ({ storeId }: Store) => !synthesizedIds.has(storeId)
    ),
  ];
};

export const useAtkomstCoreStores = (
  hasPlatformOwnerAuthorization: boolean,
  enabled = true
) =>
  useQuery<Store[]>({
    queryKey: ["atkomstStores", hasPlatformOwnerAuthorization],
    queryFn: () =>
      getAtkomstStoresWithVirtualStores(hasPlatformOwnerAuthorization),
    enabled,
    staleTime: 15 * INTERVAL.MINUTE,
    gcTime: 15 * INTERVAL.MINUTE,
  });
